import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Variables } from '../../base/variables';

export const Dialog = styled.div`
    width: 960px;
    max-width: 100%;
    background-color: ${Variables.Colors.white};
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid ${Variables.Swatches.Gray3};

    > svg {
        flex: none;
        fill: ${Variables.Swatches.Blue6};
    }
`;

export const HeaderText = styled.div`
    flex: 1;
    min-width: 0;
`;

export const Title = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 16px;
    font-weight: 600;
`;

export const Scope = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    margin-top: 2px;
`;

export const Section = styled.div`
    padding: 18px 20px;

    & + & {
        border-top: 1px solid ${Variables.Swatches.Gray2};
    }
`;

export const Eyebrow = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 10px;
`;

/** The new-configuration box: the design's tinted panel holding WHEN, THEN and TRUST. */
export const Draft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border: 1px solid ${Variables.Swatches.Gray3};
    border-radius: 8px;
    background-color: ${Variables.Swatches.Gray1};
`;

export const Row = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

export const RowTag = styled.span`
    flex: none;
    width: 52px;
    color: ${Variables.Swatches.Blue8};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const Joiner = styled.span`
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
`;

/** The code group this rule is for. Same chip as the denials table, so the two read as one thing. */
export const CodeChip = styled.span`
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    background-color: ${Variables.Swatches.Gray2};
    color: ${Variables.Swatches.Gray11};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
`;

export const ReasonText = styled.span`
    min-width: 0;
    overflow: hidden;
    color: ${Variables.Swatches.Gray8};
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Field = styled.div`
    flex: 1;
    min-width: 240px;
`;

export const NarrowField = styled.div`
    width: 170px;
`;

/** Carries a "why is this disabled" title that a disabled control cannot show itself. */
export const Slot = styled.span`
    display: inline-flex;
`;

/** Action details, indented under the THEN tag. */
export const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-left: 60px;
`;

export const Note = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    line-height: 1.5;
`;

/** The consequence of activating, stated once, plainly. */
export const Consequence = styled.div`
    color: ${Variables.Swatches.Gray9};
    font-size: 13px;
    line-height: 1.5;
`;

export const Footer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid ${Variables.Swatches.Gray2};
`;

export const TestInput = styled.div`
    width: 280px;
`;

export const TestResult = styled.span<{ failed: boolean }>`
    color: ${(props): string => (props.failed ? Variables.Colors.r50 : Variables.Swatches.Gray9)};
    font-size: 13px;
`;

export const Spacer = styled.div`
    flex: 1;
`;

export const ErrorText = styled.div`
    color: ${Variables.Colors.r50};
    font-size: 13px;
    line-height: 1.5;
`;

export const ActiveList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ActiveCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid ${Variables.Swatches.Gray3};
    border-radius: 8px;
`;

export const ActiveText = styled.div`
    flex: 1;
    min-width: 0;
`;

export const ActiveTitle = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 13px;
    font-weight: 500;
`;

export const ActiveMeta = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    margin-top: 2px;
`;

export const StateBadge = styled.span<{ workerOn: boolean }>`
    padding: 2px 7px;
    border-radius: 4px;
    background-color: ${(props): string =>
        props.workerOn ? Variables.Swatches.AlphaMiddleBlue10 : Variables.Swatches.Gray2};
    color: ${(props): string => (props.workerOn ? Variables.Swatches.Blue8 : Variables.Swatches.Gray7)};
    font-size: 11.5px;
    white-space: nowrap;
`;

export const WorkersLink = styled(Link)`
    display: inline-block;
    margin-top: 12px;
    color: ${Variables.Swatches.Blue7};
    font-size: 12px;
`;

/**
 * A match value that may be a whole payer message — the rejection dialog's `contains` list.
 *
 * `CodeChip` is `nowrap`, which is right for a CARC like `CO:197` and wrong for a sentence: a seeded
 * rejection message ran straight off the right edge of the dialog. This wraps, and breaks inside long
 * unbroken tokens (`2010BA`, run-together payer text) rather than overflowing. Additive, so the denial
 * dialog's chips are untouched.
 */
export const MatchValue = styled(CodeChip)`
    display: inline;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.6;
`;
